import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from '../../api';
import { TrendingUp, TrendingDown, ShoppingBag, IndianRupee, Ticket, MapPin, Package, Megaphone, Save } from 'lucide-react';

const BUCKETS = [
  { id: 'day',   label: 'Daily',   days: 30,  fmt: (s) => new Date(s + 'T00:00:00Z').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) },
  { id: 'week',  label: 'Weekly',  days: 84,  fmt: (s) => s.replace('-W', ' W') },  // e.g. 2026 W34
  { id: 'month', label: 'Monthly', days: 365, fmt: (s) => new Date(s + '-01T00:00:00Z').toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }) },
];

const ReportsSection = () => {
  const [bucket, setBucket] = useState('day');
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);
  const [top, setTop] = useState(null);
  const [loading, setLoading] = useState(true);
  const meta = BUCKETS.find((b) => b.id === bucket);

  const load = async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([adminApi.salesReport(bucket, days), adminApi.topReport(days)]);
      setReport(r); setTop(t);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bucket, days]);

  const switchBucket = (b) => {
    const m = BUCKETS.find((x) => x.id === b);
    setBucket(b);
    setDays(m.days);
  };

  return (
    <div className="space-y-5">
      <AnnouncementEditor />
      {/* Toggle */}
      <div className="bg-white rounded-3xl crunch-shadow-sm p-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {BUCKETS.map((b) => (
            <button key={b.id} onClick={() => switchBucket(b.id)} className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest ${bucket === b.id ? 'bg-[#1a1a1a] text-[#FFF4E6]' : 'bg-[#FFF4E6]'}`}>
              {b.label}
            </button>
          ))}
        </div>
        <div className="text-xs font-brico opacity-70">
          Range: last <span className="font-bold">{days}</span> days · buckets: <span className="font-bold uppercase">{bucket}</span>
        </div>
      </div>

      {loading || !report ? (
        <div className="bg-white rounded-3xl crunch-shadow p-10 text-center font-brico opacity-70">Loading…</div>
      ) : (
        <>
          <SummaryStrip s={report.summary} />
          <ChartCard report={report} labelFmt={meta.fmt} />
          <div className="grid md:grid-cols-3 gap-4">
            <TopCard title="Top Products"  icon={Package}    rows={top?.products || []} keyLabel="title" cols={[
              { label: 'Units',   render: (r) => r.units },
              { label: 'Revenue', render: (r) => `₹${r.revenue}` },
            ]} empty="No product sales yet." />
            <TopCard title="Top Cities"    icon={MapPin}     rows={top?.cities || []}   keyLabel="city" cols={[
              { label: 'Orders',  render: (r) => r.orders },
              { label: 'Revenue', render: (r) => `₹${r.revenue}` },
            ]} empty="No shipments yet." />
            <TopCard title="Top Coupons"   icon={Ticket}     rows={top?.coupons || []}  keyLabel="code" cols={[
              { label: 'Orders',   render: (r) => r.orders },
              { label: 'Revenue',  render: (r) => `₹${r.revenue}` },
              { label: 'Discount', render: (r) => `−₹${r.discount}` },
            ]} empty="No coupon redemptions yet." />
          </div>
        </>
      )}
    </div>
  );
};

const SummaryStrip = ({ s }) => {
  const growth = s.growth_pct;
  const growthPositive = growth != null && growth >= 0;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatBig label="Revenue" value={`₹${s.revenue.toLocaleString('en-IN')}`} icon={IndianRupee} color="#FFD84D" />
      <StatBig label="Orders"  value={s.orders} icon={ShoppingBag} color="#FFE9C4" />
      <StatBig label="AOV"     value={`₹${s.aov}`} icon={TrendingUp} color="#D8F0EC" />
      <div className="rounded-2xl crunch-shadow-sm p-4 md:p-5 bg-white">
        <div className="text-xs font-bold uppercase tracking-widest opacity-70 flex items-center gap-1">
          {growthPositive ? <TrendingUp size={14} className="text-[#2A9D8F]" /> : <TrendingDown size={14} className="text-[#E63946]" />} vs previous
        </div>
        <div className={`font-display text-3xl md:text-4xl mt-1 ${growthPositive ? 'text-[#2A9D8F]' : 'text-[#E63946]'}`}>
          {growth == null ? '—' : `${growthPositive ? '+' : ''}${growth}%`}
        </div>
        <div className="text-[10px] font-brico opacity-60 mt-1">Prev revenue: ₹{s.prev_revenue.toLocaleString('en-IN')}</div>
      </div>
    </div>
  );
};

const StatBig = ({ label, value, icon: Icon, color }) => (
  <div className="rounded-2xl crunch-shadow-sm p-4 md:p-5" style={{ background: color }}>
    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest opacity-80"><Icon size={14} /> {label}</div>
    <div className="font-display text-3xl md:text-4xl mt-2">{value}</div>
  </div>
);

// SVG bar+line chart
const ChartCard = ({ report, labelFmt }) => {
  const series = report.series;
  const W = 780, H = 260, PAD_L = 44, PAD_R = 16, PAD_T = 16, PAD_B = 40;
  const cw = W - PAD_L - PAD_R, ch = H - PAD_T - PAD_B;
  const maxRev = Math.max(1, ...series.map((s) => s.revenue));
  const maxOrders = Math.max(1, ...series.map((s) => s.orders));

  const barW = series.length ? Math.max(6, Math.floor(cw / series.length) - 4) : 0;

  // Nice Y ticks
  const ticks = useMemo(() => {
    const nice = (v) => {
      const exp = Math.pow(10, Math.floor(Math.log10(v || 1)));
      const n = v / exp;
      const cand = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
      return cand * exp;
    };
    const step = nice(maxRev / 4);
    const arr = [];
    for (let i = 0; i * step <= maxRev * 1.05; i++) arr.push(i * step);
    return arr;
  }, [maxRev]);

  // Line points (orders scaled)
  const pts = series.map((s, i) => {
    const x = PAD_L + i * (cw / Math.max(1, series.length)) + (cw / Math.max(1, series.length)) / 2;
    const y = PAD_T + ch - (s.orders / maxOrders) * ch;
    return { x, y, s };
  });

  return (
    <div className="bg-white rounded-3xl crunch-shadow p-5 md:p-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-display text-2xl uppercase">Revenue & Orders</div>
        <div className="flex items-center gap-4 text-xs font-brico">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#E63946]" /> Revenue (₹)</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#2A9D8F]" /> Orders</span>
        </div>
      </div>

      {series.length === 0 ? (
        <div className="mt-6 text-center font-brico opacity-70 py-10">No sales in this period yet.</div>
      ) : (
        <div className="mt-4 w-full overflow-x-auto">
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet">
            {/* Y grid + labels */}
            {ticks.map((t, i) => {
              const y = PAD_T + ch - (t / (ticks[ticks.length - 1] || 1)) * ch;
              return (
                <g key={i}>
                  <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="#00000012" />
                  <text x={PAD_L - 6} y={y + 4} textAnchor="end" fontSize="10" fill="#00000080" fontFamily="'Bricolage Grotesque', sans-serif">₹{Math.round(t)}</text>
                </g>
              );
            })}
            {/* Bars */}
            {series.map((s, i) => {
              const cx = PAD_L + i * (cw / series.length) + (cw / series.length) / 2;
              const h = ((s.revenue) / (ticks[ticks.length - 1] || 1)) * ch;
              const x = cx - barW / 2;
              const y = PAD_T + ch - h;
              return (
                <g key={i}>
                  <rect x={x} y={y} width={barW} height={h} rx="4" fill="#E63946" opacity="0.9">
                    <title>{`${labelFmt(s.date)} · ₹${s.revenue} · ${s.orders} orders`}</title>
                  </rect>
                </g>
              );
            })}
            {/* Orders line */}
            <path
              d={pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')}
              stroke="#2A9D8F" strokeWidth="2" fill="none"
            />
            {pts.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r="3" fill="#2A9D8F">
                <title>{`${labelFmt(p.s.date)} · ${p.s.orders} orders`}</title>
              </circle>
            ))}
            {/* X labels */}
            {series.map((s, i) => {
              if (series.length > 15 && i % Math.ceil(series.length / 12) !== 0 && i !== series.length - 1) return null;
              const cx = PAD_L + i * (cw / series.length) + (cw / series.length) / 2;
              return (
                <text key={`xl-${i}`} x={cx} y={H - 14} textAnchor="middle" fontSize="10" fill="#00000090" fontFamily="'Bricolage Grotesque', sans-serif">
                  {labelFmt(s.date)}
                </text>
              );
            })}
          </svg>
        </div>
      )}
    </div>
  );
};

const TopCard = ({ title, icon: Icon, rows, keyLabel, cols, empty }) => (
  <div className="bg-white rounded-3xl crunch-shadow p-5">
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} className="text-[#E63946]" />
      <div className="font-display text-lg uppercase">{title}</div>
    </div>
    {rows.length === 0 ? (
      <div className="text-sm font-brico opacity-70 py-6 text-center">{empty}</div>
    ) : (
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between gap-2 border-b border-black/5 last:border-b-0 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-6 h-6 rounded-full bg-[#FFF4E6] text-[10px] font-bold flex items-center justify-center flex-shrink-0">#{i + 1}</span>
              <span className="font-brico font-bold truncate">{r[keyLabel] || '—'}</span>
            </div>
            <div className="flex items-center gap-3 text-sm font-brico flex-shrink-0">
              {cols.map((c, k) => (
                <div key={k} className="text-right">
                  <div className="text-[10px] uppercase tracking-widest opacity-60">{c.label}</div>
                  <div className="font-bold">{c.render(r)}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const AnnouncementEditor = () => {
  const [f, setF] = useState({ enabled: false, text: '', link: '', link_label: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    adminApi.getAnnouncement().then(setF).finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.setAnnouncement(f);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;
  const input = 'w-full px-3 py-2 rounded-xl border-2 border-[#1a1a1a] font-brico bg-white focus:outline-none focus:ring-2 focus:ring-[#E63946]';

  return (
    <form onSubmit={save} className="bg-white rounded-3xl crunch-shadow p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Megaphone size={18} className="text-[#E63946]" />
          <div className="font-display text-lg uppercase">Site Announcement Bar</div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm font-brico font-bold cursor-pointer">
          <input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} className="w-5 h-5" />
          {f.enabled ? 'ON' : 'OFF'}
        </label>
      </div>
      <div className="mt-3 grid md:grid-cols-3 gap-3">
        <input required={f.enabled} placeholder="Announcement text" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} className={`${input} md:col-span-3`} />
        <input placeholder="Link (optional, e.g. /play)" value={f.link || ''} onChange={(e) => setF({ ...f, link: e.target.value })} className={input} />
        <input placeholder="Link label (e.g. Play now)" value={f.link_label || ''} onChange={(e) => setF({ ...f, link_label: e.target.value })} className={input} />
        <button type="submit" disabled={saving} className="btn-chunky primary justify-center">
          <Save size={14} /> {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save banner'}
        </button>
      </div>
      {f.enabled && f.text && (
        <div className="mt-4 rounded-xl overflow-hidden">
          <div className="text-[10px] font-bold uppercase tracking-widest opacity-60 mb-1">Preview</div>
          <div className="bg-gradient-to-r from-[#E63946] via-[#F4A300] to-[#FFD84D] px-4 py-2 text-sm font-brico font-bold flex items-center justify-center gap-3">
            <Megaphone size={14} /> {f.text} {f.link && f.link_label && <span className="underline">{f.link_label} →</span>}
          </div>
        </div>
      )}
    </form>
  );
};

export default ReportsSection;
